import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SingleBeetDeliveryReport from '../../presentation/reports/SingleBeetDeliveryReport/SingleBeetDeliveryReport';
import { useI18n } from '../../hooks/useI18n';
import { TranslationKeys } from '../../i18n/locales/defaultResources';
import PageHeader from '../../presentation/layout/PageHeader/PageHeader';
import { MainContainer } from '../../styles/styles';
import {
  CropConnectRoleDefinitions,
  DeliveryPlanItemDto,
  DocumentFolder,
  DocumentSnapshotDto,
} from '../../services/cropConnectApiClient';
import SecurityTrim, {
  securityTrimConditionsMet,
} from '../../services/auth/SecurityTrim';
import ActionButton from '../../presentation/components/ActionButton/ActionButton';
import {
  DetailViewWrapper,
  LeftPlaceholder,
  MainContentWrapper,
  RightWidgetContainer,
  StyledActionButtonContainer,
} from './styles';
import MyDeliveryPlanTable from '../../presentation/features/delivery/MyDeliveryPlanTable/MyDeliveryPlanTable';
import {
  useContextStore,
  useDeliveryPlanStore,
  useDocumentsStore,
} from '../../stores/storeRoot';
import { DeliveryPlanRow } from '../../presentation/features/delivery/MyDeliveryPlanTable/types';
import { handleDownloadClick } from '../../helpers/printTableUtils';
import {
  administrationPath,
  individualDocumentsLiefermitteilungenPath,
} from '../../routes';
import Loader from '../../presentation/components/Loader/Loader';
import { mapLocaleAccordingToNzRules } from '../../helpers/localeFormatter';
import GeneralDocumentsComponent from '../../presentation/features/general_documents/GeneralDocumentsComponent/GeneralDocumentsComponent';
import SampleFrequencyWidget from '../../presentation/features/databox/SampleFrequency/SampleFrequency';
import DocumentsWidget from '../../presentation/features/documents/DocumentsWidget/DocumentsWidget';

interface MyCampaignsProps {
  isDeliveryPlan?: boolean;
}

const mapToDocument = (doc: DocumentSnapshotDto) => ({
  name: doc.name || '',
  url: '',
  id: doc.id || '',
});

const MyCampaigns: React.FC<MyCampaignsProps> = ({
  isDeliveryPlan = false,
}) => {
  const { t } = useI18n();
  const { getDocumentsPageWithPrefilter } = useDocumentsStore();
  const navigate = useNavigate();
  const [isMainView, setIsMainView] = useState(!isDeliveryPlan);
  const { deliveryPlan, isLoading, fetchDeliveryPlans } =
    useDeliveryPlanStore();
  const {
    activeBpNumber,
    activeBpNumberCountry,
    globalAssignment,
    nordzuckerInternalUser,
    businessPartnerAssignment,
  } = useContextStore();

  const [tableData, setTableData] = useState<DeliveryPlanRow[]>([]);
  const [individualDocuments, setIndividualDocuments] = useState<
    DocumentSnapshotDto[]
  >([]);

  useEffect(() => {
    if (!isMainView) {
      fetchDeliveryPlans();
    }
  }, [isMainView, fetchDeliveryPlans, activeBpNumber]);

  useEffect(() => {
    if (deliveryPlan && deliveryPlan.plannedDeliveries) {
      const mappedData = mapDeliveryPlansToTableData(
        deliveryPlan.plannedDeliveries
      );
      setTableData(mappedData);
    } else {
      setTableData([]);
    }
  }, [deliveryPlan]);

  useEffect(() => {
    setIndividualDocuments([]);
    getDocumentsPageWithPrefilter(
      DocumentFolder.MyPayments,
      'liefermitteilungen',
      3
    ).then((d) => {
      setIndividualDocuments(d?.documents || []);
    });
  }, [activeBpNumber, getDocumentsPageWithPrefilter]);

  const handleDeliveryPlanView = () => {
    setIsMainView(false);
  };

  const handleBackToOverview = () => {
    setIsMainView(true);
  };

  const handleSettingsClick = () => {
    navigate(administrationPath);
  };

  const mapDeliveryPlansToTableData = (
    deliveryPlanItems: DeliveryPlanItemDto[]
  ): DeliveryPlanRow[] => {
    return deliveryPlanItems.map((delivery) => {
      const fieldNameValue = delivery.isSelfLoader
        ? String(t(TranslationKeys.my_delivery_plan_own_choice))
        : delivery.fieldName || '';

      return {
        loadDate: delivery.loadDate || '',
        loadWeek: delivery.loadWeek || 0,
        fieldName: fieldNameValue,
        acreage: delivery.acreage,
        loadQuantity: delivery.loadQuantity || 0,
        transportingCompany: delivery.transportingCompany || '',
        transportingCompanyName: delivery.transportingCompanyName || '',
        pileCoverer: delivery.pileCoverer || '',
        pileCovererName: delivery.pileCovererName || '',
        isSelfLoader: delivery.isSelfLoader || false,
        isSelfCoverer: delivery.isSelfCoverer || false,
        exactDateVisible: delivery.exactDateVisible || false,
      };
    });
  };

  return (
    <MainContainer>
      <PageHeader
        title={t(TranslationKeys.my_campaign_pageHeader)}
        extraButton={
          isMainView && (
            <ActionButton
              variant="accept-blue"
              buttonText={t(TranslationKeys.my_campaign_deliveryPlan_button)}
              onClickAction={handleDeliveryPlanView}
            />
          )
        }
      />
      {isMainView ? (
        <MainContentWrapper>
          <LeftPlaceholder>
            <SecurityTrim
              requiredBusinessPartnerRoles={[CropConnectRoleDefinitions.Grower]}
            >
              <SingleBeetDeliveryReport />
            </SecurityTrim>
          </LeftPlaceholder>
          <RightWidgetContainer>
            <SampleFrequencyWidget />
            <GeneralDocumentsComponent parent="my-campaign" />
            <SecurityTrim
              requiredBusinessPartnerRoles={[
                CropConnectRoleDefinitions.Grower_DE,
                CropConnectRoleDefinitions.Grower_Organic_DE,
              ]}
            >
              <DocumentsWidget
                documents={individualDocuments.map(mapToDocument)}
                onViewAllClick={() =>
                  navigate(individualDocumentsLiefermitteilungenPath)
                }
                title={
                  t(
                    TranslationKeys.individual_documents_widget_liefermitteilungen_title
                  ) as string
                }
              />
            </SecurityTrim>
          </RightWidgetContainer>
        </MainContentWrapper>
      ) : (
        <DetailViewWrapper>
          <StyledActionButtonContainer>
            <ActionButton
              variant="link-button"
              buttonText={t(TranslationKeys.common_back_to_overview)}
              onClickAction={handleBackToOverview}
              back={true}
            />
          </StyledActionButtonContainer>
          {isLoading ? (
            <Loader isFullScreen={false} size={40} />
          ) : (
            <MyDeliveryPlanTable
              tableHeader={t(TranslationKeys.my_campaign_delivery_plan)}
              data={tableData}
              countryIsoCode={mapLocaleAccordingToNzRules(
                activeBpNumberCountry
              )}
              actions={{
                showDownload: true,
                showSettings: securityTrimConditionsMet(
                  {
                    onlyNordzuckerInternalUsers: true,
                    requiredGlobalRoles: [
                      CropConnectRoleDefinitions.GlobalAdmin,
                    ],
                  },
                  globalAssignment,
                  businessPartnerAssignment,
                  nordzuckerInternalUser
                ),
                onDownloadClick: () => {
                  const translations = {
                    title: t(TranslationKeys.my_campaign_delivery_plan),
                    deliveryDate: t(
                      TranslationKeys.my_delivery_plan_column_delivery_date
                    ),
                    fieldName: t(
                      TranslationKeys.my_delivery_plan_column_field_name
                    ),
                    area: t(TranslationKeys.my_delivery_plan_column_area_ha),
                    load: t(TranslationKeys.my_delivery_plan_column_load_tons),
                    transporter: t(
                      TranslationKeys.my_delivery_plan_column_transporter
                    ),
                    pileCoverer: t(
                      TranslationKeys.my_delivery_plan_column_pile_coverer
                    ),
                    calendarWeek: t(TranslationKeys.calendar_week),
                    ownChoice: t(TranslationKeys.my_delivery_plan_own_choice),
                  };

                  handleDownloadClick(
                    tableData,
                    mapLocaleAccordingToNzRules(activeBpNumberCountry),
                    translations,
                    activeBpNumber
                  );
                },

                onSettingsClick: handleSettingsClick,
              }}
              deliveryPlanNotVisibleText={deliveryPlan?.notVisibleMessage}
              deliveryPlanVisible={deliveryPlan?.deliveryPlanVisible ?? true}
              key={activeBpNumber}
            />
          )}
        </DetailViewWrapper>
      )}
    </MainContainer>
  );
};

export default MyCampaigns;
